import React, { ReactElement } from 'react';

export type RetailerUrlFormat = {
  retailer_home: string;
  retailer_name: string;
  retailer_db_name: string;
  requirements: ReactElement[];
  examples: ReactElement[];
  regex: string[];
};

export const retailerUrlFormats: ReadonlyArray<RetailerUrlFormat> = [
  {
    retailer_home: 'academy.com',
    retailer_name: 'Academy',
    retailer_db_name: 'Academy',
    regex: [],
    requirements: [
      (
        <li key="academy-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">
            https://www.academy.com/p/
          </span>
          .
        </li>
      ),
      (
        <li key="academy-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must include the product&rsquo;s name or identifier after the
          {' '}
          <span className="url-fragment">
            /p/
          </span>
          {' '}
          part of the URL.
        </li>
      ),
      (
        <li key="academy-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          may end with
          {' '}
          <span className="url-fragment">
            ?sku=[Retailer SKU]
          </span>
          {' '}
          if the product is a variant.
        </li>
      ),
    ],
    examples: [
      (
        <li key="academy-example-1">
          <span className="example-url">
            https://www.academy.com/p/product-info
          </span>
        </li>
      ),
      (
        <li key="academy-example-2">
          <span className="example-url">
            https://www.academy.com/p/product-info?sku=a-retailer-sku
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'acehardware.com',
    retailer_name: 'Ace Hardware',
    retailer_db_name: 'Ace Hardware',
    regex: [],
    requirements: [
      (
        <li key="acehardware-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          , which is a 4–7 digit number.
        </li>
      ),
      (
        <li key="acehardware-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          may include product category or other descriptive information in the path.
        </li>
      ),
    ],
    examples: [
      (
        <li key="acehardware-example-1">
          <span className="example-url">
            https://www.acehardware.com/departments/outdoor-living/grills-and-smokers/gas-grills/8863581
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'shop.advanceautoparts.com',
    retailer_name: 'Advance Auto Parts',
    retailer_db_name: 'Advance Auto Parts',
    regex: [],
    requirements: [
      (
        <li key="advanceauto-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="advanceauto-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          may include product information, such as a product name, before the SKU.
        </li>
      ),
      (
        <li key="advanceauto-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">
            https://shop.advanceautoparts.com/p/
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="advanceauto-example-1">
          <span className="example-url">
            https://shop.advanceautoparts.com/p/diehard-gold-battery-35-group-size-640-cca-800-ca-100-minute-reserve-capacity-maximum-starting-power-35-2/2130004-P
          </span>
        </li>
      ),
      (
        <li key="advanceauto-example-2">
          <span className="example-url">
            https://shop.advanceautoparts.com/p/x/2130004-P
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.ae',
    retailer_name: 'Amazon AE',
    retailer_db_name: 'Amazon.ae',
    regex: [],
    requirements: [
      (
        <li key="amazonae-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">
            ASIN
          </span>
          .
        </li>
      ),
      (
        <li key="amazonae-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazonae-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            ?th=1
          </span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">
            ?th=1&amp;psc=1
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazonae-example-1">
          <span className="example-url">
            https://www.amazon.ae/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazonae-example-2">
          <span className="example-url">
            https://www.amazon.ae/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.ca',
    retailer_name: 'Amazon CA',
    retailer_db_name: 'Amazon.ca',
    regex: [],
    requirements: [
      (
        <li key="amazonca-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">
            ASIN
          </span>
          .
        </li>
      ),
      (
        <li key="amazonca-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazonca-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            ?th=1
          </span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">
            ?th=1&amp;psc=1
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazonca-example-1">
          <span className="example-url">
            https://www.amazon.ca/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazonca-example-2">
          <span className="example-url">
            https://www.amazon.ca/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.co.uk',
    retailer_name: 'Amazon UK',
    retailer_db_name: 'Amazon.co.uk',
    regex: [],
    requirements: [
      (
        <li key="amazoncouk-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">
            ASIN
          </span>
          .
        </li>
      ),
      (
        <li key="amazoncouk-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazoncouk-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            ?th=1
          </span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">
            ?th=1&amp;psc=1
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazoncouk-example-1">
          <span className="example-url">
            https://www.amazon.co.uk/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazoncouk-example-2">
          <span className="example-url">
            https://www.amazon.co.uk/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.com',
    retailer_name: 'Amazon US',
    retailer_db_name: 'Amazon.com',
    regex: [],
    requirements: [
      (
        <li key="amazon-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">ASIN</span>
          .
        </li>
      ),
      (
        <li key="amazon-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazon-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?th=1</span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">?th=1&amp;psc=1</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazon-example-1">
          <span className="example-url">https://www.amazon.com/dp/B0B33TZX29?th=1</span>
        </li>
      ),
      (
        <li key="amazon-example-2">
          <span className="example-url">https://www.amazon.com/dp/B0CL4NC1H5?th=1&amp;psc=1</span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.com.br',
    retailer_name: 'Amazon BR',
    retailer_db_name: 'Amazon.com.br',
    regex: [],
    requirements: [
      (
        <li key="amazoncombr-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">ASIN</span>
          .
        </li>
      ),
      (
        <li key="amazoncombr-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazoncombr-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?th=1</span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">?th=1&amp;psc=1</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazoncombr-example-1">
          <span className="example-url">
            https://www.amazon.com.br/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazoncombr-example-2">
          <span className="example-url">
            https://www.amazon.com.br/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.com.mx',
    retailer_name: 'Amazon MX',
    retailer_db_name: 'Amazon.com.mx',
    regex: [],
    requirements: [
      (
        <li key="amazoncommx-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">ASIN</span>
          .
        </li>
      ),
      (
        <li key="amazoncommx-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazoncommx-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?th=1</span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">?th=1&amp;psc=1</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazoncommx-example-1">
          <span className="example-url">
            https://www.amazon.com.mx/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazoncommx-example-2">
          <span className="example-url">
            https://www.amazon.com.mx/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.de',
    retailer_name: 'Amazon DE',
    retailer_db_name: 'Amazon.de',
    regex: [],
    requirements: [
      (
        <li key="amazonde-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">ASIN</span>
          .
        </li>
      ),
      (
        <li key="amazonde-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazonde-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?th=1</span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">?th=1&amp;psc=1</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazonde-example-1">
          <span className="example-url">
            https://www.amazon.de/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazonde-example-2">
          <span className="example-url">
            https://www.amazon.de/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.es',
    retailer_name: 'Amazon ES',
    retailer_db_name: 'Amazon.es',
    regex: [],
    requirements: [
      (
        <li key="amazones-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">ASIN</span>
          .
        </li>
      ),
      (
        <li key="amazones-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazones-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?th=1</span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">?th=1&amp;psc=1</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazones-example-1">
          <span className="example-url">
            https://www.amazon.es/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazones-example-2">
          <span className="example-url">
            https://www.amazon.es/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.fr',
    retailer_name: 'Amazon FR',
    retailer_db_name: 'Amazon.fr',
    regex: [],
    requirements: [
      (
        <li key="amazonfr-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">ASIN</span>
          .
        </li>
      ),
      (
        <li key="amazonfr-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazonfr-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?th=1</span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">?th=1&amp;psc=1</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazonfr-example-1">
          <span className="example-url">
            https://www.amazon.fr/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazonfr-example-2">
          <span className="example-url">
            https://www.amazon.fr/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'amazon.it',
    retailer_name: 'Amazon IT',
    retailer_db_name: 'Amazon.it',
    regex: [],
    requirements: [
      (
        <li key="amazonit-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">ASIN</span>
          .
        </li>
      ),
      (
        <li key="amazonit-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must have all tracking information removed.
        </li>
      ),
      (
        <li key="amazonit-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?th=1</span>
          {' '}
          if the product has no variants. Otherwise, it must end with
          {' '}
          <span className="url-fragment">?th=1&amp;psc=1</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="amazonit-example-1">
          <span className="example-url">
            https://www.amazon.it/dp/B0B33TZX29?th=1
          </span>
        </li>
      ),
      (
        <li key="amazonit-example-2">
          <span className="example-url">
            https://www.amazon.it/dp/B0CL4NC1H5?th=1&amp;psc=1
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'autozone.com',
    retailer_name: 'Autozone',
    retailer_db_name: 'Autozone',
    regex: [],
    requirements: [
      (
        <li key="autozone-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must end with the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="autozone-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          may contain product information.
        </li>
      ),
      (
        <li key="autozone-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">https://www.autozone.com/</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="autozone-example-1">
          <span className="example-url">
            https://www.autozone.com/external-engine/motor-oil/p/castrol-gtx-high-mileage-sae-5w-30-conventional-motor-oil-5-quarts/421275_0_0
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'bedbathandbeyond.com',
    retailer_name: 'Bed Bath & Beyond',
    retailer_db_name: 'Bed Bath and Beyond',
    regex: [],
    requirements: [
      (
        <li key="bedbathandbeyond-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          will not contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="bedbathandbeyond-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must contain
          {' '}
          <span className="url-fragment">opre=1</span>
          {' '}
          as the first query parameter after
          {' '}
          <span className="url-fragment">product.html</span>
          . The first query uses
          {' '}
          <span className="url-fragment">?</span>
          , subsequent ones use
          {' '}
          <span className="url-fragment">&amp;</span>
          .
        </li>
      ),
      (
        <li key="bedbathandbeyond-requirement-3">
          If the item is a variant, the
          {' '}
          <span className="product-field">Product URL</span>
          {' '}
          must have one or more
          {' '}
          <span className="url-fragment">option=[8 digit #]</span>
          {' '}
          values as query parameters.
        </li>
      ),
    ],
    examples: [
      (
        <li key="bedbathandbeyond-example-1">
          Non-variant:
          {' '}
          <span className="example-url">
            https://www.bedbathandbeyond.com/Home-Garden/Round-Style-Run-In-Shelter/16695949/product.html?opre=1
          </span>
        </li>
      ),
      (
        <li key="bedbathandbeyond-example-2">
          Non-variant:
          {' '}
          <span className="example-url">
            https://www.bedbathandbeyond.com/16695949/product.html?opre=1
          </span>
        </li>
      ),
      (
        <li key="bedbathandbeyond-example-3">
          Variant:
          {' '}
          <span className="example-url">
            https://www.bedbathandbeyond.com/Home-Garden/TEMPUR-Ergo-Adjustable-Base/22643828/product.html?opre=1&option=37706610
          </span>
        </li>
      ),
      (
        <li key="bedbathandbeyond-example-4">
          Variant:
          {' '}
          <span className="example-url">
            https://www.bedbathandbeyond.com/Home-Garden/TEMPUR-Ergo-Adjustable-Base/22643828/product.html?opre=1&option=37706610&option=12345678
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'bestbuy.com',
    retailer_name: 'Best Buy',
    retailer_db_name: 'Best Buy',
    regex: [],
    requirements: [
      (
        <li key="bestbuy-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must use one of the three formats shown below, and must contain the
          {' '}
          <span className="product-field">
            Best Buy SKU
          </span>
          : the 7- or 8-digit number shown next to
          {' '}
          <span className="url-fragment">
            SKU
          </span>
          {' '}
          on the product page.
        </li>
      ),
      (
        <li key="bestbuy-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must use the
          {' '}
          <span className="product-field">
            SKU
          </span>
          , not the
          {' '}
          <span className="product-field">
            BSIN
          </span>
          {' '}
          (the 10-character code beginning with
          {' '}
          <span className="url-fragment">
            J
          </span>
          ).
        </li>
      ),
      (
        <li key="bestbuy-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with the
          {' '}
          <span className="product-field">
            SKU
          </span>
          , exactly as shown in the examples.
        </li>
      ),
    ],
    examples: [
      (
        <li key="bestbuy-example-1">
          <span className="example-url">
            https://www.bestbuy.com/product/sku/12843005
          </span>
        </li>
      ),
      (
        <li key="bestbuy-example-2">
          <span className="example-url">
            https://www.bestbuy.com/product/alamont-home-kai-all-weather-commercial-bar-stool-with-removable-back-poly-seat-white-gray/12843005
          </span>
        </li>
      ),
      (
        <li key="bestbuy-example-3">
          <span className="example-url">
            https://www.bestbuy.com/site/12843005.p?skuId=12843005
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'biglots.com',
    retailer_name: 'Big Lots',
    retailer_db_name: 'Big Lots',
    regex: [],
    requirements: [
      (
        <li key="biglots-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">https://www.biglots.com/product/</span>
          .
        </li>
      ),
      (
        <li key="biglots-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must include descriptive product text after
          {' '}
          <span className="url-fragment">/product/</span>
          , such as the product&rsquo;s name.
        </li>
      ),
      (
        <li key="biglots-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with a unique product ID that begins with
          {' '}
          <span className="url-fragment">p</span>
          {' '}
          followed by 9–10 letters or numbers.
        </li>
      ),
    ],
    examples: [
      (
        <li key="biglots-example-1">
          <span className="example-url">
            https://www.biglots.com/product/memory-foam-gel-standard-pillow/p810436274
          </span>
        </li>
      ),
      (
        <li key="biglots-example-2">
          <span className="example-url">
            https://www.biglots.com/product/7-piece-comforter-set/p810732846
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'bjs.com',
    retailer_name: 'BJ\'s',
    retailer_db_name: 'BJs',
    regex: [],
    requirements: [
      (
        <li key="bjs-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">https://www.bjs.com/product/</span>
          .
        </li>
      ),
      (
        <li key="bjs-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must include descriptive product text after
          {' '}
          <span className="url-fragment">/product/</span>
          , such as the product&rsquo;s name.
        </li>
      ),
      (
        <li key="bjs-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with an 18–20 digit product ID.
        </li>
      ),
    ],
    examples: [
      (
        <li key="bjs-example-1">
          <span className="example-url">
            https://www.bjs.com/product/sealy-duochill-cooling-memory-foam-bed-pillow/3000000000003077819
          </span>
        </li>
      ),
      (
        <li key="bjs-example-2">
          <span className="example-url">
            https://www.bjs.com/product/members-mark-eco-friendly-ultra-paper-plates-204-ct/3000000000001259189
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'farmandfleet.com',
    retailer_name: 'Farm and Fleet',
    retailer_db_name: 'Blains Farm and Fleet',
    regex: [],
    requirements: [
      (
        <li key="farmandfleet-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must include the
          {' '}
          <span className="product-field">Retailer SKU</span>
          , which is a 6–7 digit number near the beginning of the URL.
        </li>
      ),
      (
        <li key="farmandfleet-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must contain a short product description made of
          lowercase words separated by hyphens after the SKU.
        </li>
      ),
      (
        <li key="farmandfleet-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">.html</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="farmandfleet-example-1">
          <span className="example-url">
            https://www.farmandfleet.com/products/1007144-oklahoma-joes-highland-reverse-flow-smoker.html
          </span>
        </li>
      ),
      (
        <li key="farmandfleet-example-2">
          <span className="example-url">
            https://www.farmandfleet.com/products/1234567-heavy-duty-tool-storage-cabinet.html
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'fleetfarm.com',
    retailer_name: 'Fleet Farm',
    retailer_db_name: 'Fleet Farm',
    regex: [],
    requirements: [
      (
        <li key="fleetfarm-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          may not contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="fleetfarm-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must contain product information.
        </li>
      ),
      (
        <li key="fleetfarm-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          cannot be shortened.
        </li>
      ),
    ],
    examples: [
      (
        <li key="fleetfarm-example-1">
          <span className="example-url">
            https://www.fleetfarm.com/detail/char-broil-20-in-universal-liquid-propane-grill-hose-regulator/0000000061603
          </span>
        </li>
      ),
      (
        <li key="fleetfarm-example-2">
          <span className="example-url">
            https://www.fleetfarm.com/detail/masterbuilt-mps-340g-thermotemp-xl-propane-smoker/0000000370356?bc=11315|11423|11426
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'grainger.com',
    retailer_name: 'Grainger',
    retailer_db_name: 'Grainger',
    regex: [],
    requirements: [
      (
        <li key="grainger-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must end with the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="grainger-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must contain product information.
        </li>
      ),
    ],
    examples: [
      (
        <li key="grainger-example-1">
          <span className="example-url">
            https://www.grainger.com/product/SUNCAST-Deck-Box-73-gal-15X398
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'hdsupplysolutions.com',
    retailer_name: 'HD Supply',
    retailer_db_name: 'HD Supply',
    regex: [],
    requirements: [
      (
        <li key="hdsupplysolutionscom-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">
            https://hdsupplysolutions.com/p/
          </span>
          .
        </li>
      ),
      (
        <li key="hdsupplysolutionscom-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          may contain product information.
        </li>
      ),
    ],
    examples: [
      (
        <li key="hdsupplysolutionscom-example-1">
          <span className="example-url">
            https://hdsupplysolutions.com/p/televisions-accessories-00-35-30/rca-40-in-1080p-pro-idiom-hospitality-tv-p220952
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'homedepot.com',
    retailer_name: 'The Home Depot',
    retailer_db_name: 'Home Depot',
    regex: [],
    requirements: [
      (
        <li key="homedepot-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="homedepot-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          may contain product information.
        </li>
      ),
    ],
    examples: [
      (
        <li key="homedepot-example-1">
          <span className="example-url">
            https://www.homedepot.com/p/First-America-PTAC-Wall-Sleeve-Trim-Kit-SLEEVEMOLDING/206634991
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'huffy.com',
    retailer_name: 'Huffy',
    retailer_db_name: 'Huffy',
    regex: [],
    requirements: [
      (
        <li key="huffy-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="huffy-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must contain product information.
        </li>
      ),
    ],
    examples: [
      (
        <li key="huffy-example-1">
          <span className="example-url">
            https://www.huffy.com/sanford-womens-cruiser-bike-26971/
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'kohls.com',
    retailer_name: 'Kohls',
    retailer_db_name: 'Kohls',
    regex: [],
    requirements: [
      (
        <li key="kohls-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">
            https://www.kohls.com/shopnext-web-findability/product/prd-
          </span>
          , followed by the 6–8 digit product ID and a
          {' '}
          <span className="url-fragment">/</span>
          .
        </li>
      ),
      (
        <li key="kohls-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">?skuId=[Retailer SKU]</span>
          {' '}
          when the product has variants (e.g. color, size, pack count). Without it,
          the default variant is tracked. No other query parameters are allowed.
        </li>
      ),
      (
        <li key="kohls-requirement-3">
          URLs in the older
          {' '}
          <span className="url-fragment">https://www.kohls.com/product/prd-</span>
          {' '}
          format are not accepted.
        </li>
      ),
    ],
    examples: [
      (
        <li key="kohls-example-1">
          <span className="example-url">
            https://www.kohls.com/shopnext-web-findability/product/prd-1811976/?skuId=
            <b>95521772</b>
          </span>
          {' '}
          (product with variants)
        </li>
      ),
      (
        <li key="kohls-example-2">
          <span className="example-url">
            https://www.kohls.com/shopnext-web-findability/product/prd-8013880/
          </span>
          {' '}
          (single-variant product)
        </li>
      ),
    ],
  },
  {
    retailer_home: 'lowes.com',
    retailer_name: "Lowe's",
    retailer_db_name: "Lowe's",
    regex: [],
    requirements: [
      (
        <li key="lowes-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain product information, followed by a
          {' '}
          <span className="url-fragment">/</span>
          {' '}
          and a 6–10 digit
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="lowes-example-1">
          <span className="example-url">
            https://www.lowes.com/pd/CRAFTSMAN-SB210-21-in-123-cc-Single-Stage-Gas-Snow-Blower-with-Pull-Start/1000602799
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'meijer.com',
    retailer_name: 'Meijer',
    retailer_db_name: 'Meijer',
    regex: [],
    requirements: [
      (
        <li key="meijer-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">
            https://www.meijer.com/shopping/product
          </span>
          .
        </li>
      ),
      (
        <li key="meijer-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must contain product information, followed by a
          {' '}
          <span className="url-fragment">/</span>
          {' '}
          and a number.
        </li>
      ),
      (
        <li key="meijer-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">.html</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="meijer-example-1">
          <span className="example-url">
            https://www.meijer.com/shopping/product/samsung-65-uhd-smart-tv/88727683078.html
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'menards.com',
    retailer_name: 'Menards',
    retailer_db_name: 'Menards',
    regex: [],
    requirements: [
      (
        <li key="menards-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          will
          {' '}
          <strong>not</strong>
          {' '}
          contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          , it will contain an internet identification string instead.
        </li>
      ),
      (
        <li key="menards-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          may contain product information.
        </li>
      ),
      (
        <li key="menards-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          can be shortened.
        </li>
      ),
    ],
    examples: [
      (
        <li key="menards-example-1">
          Short Version:
          {' '}
          <span className="example-url">
            https://www.menards.com/main/p-1444441253821-c-10109.htm
          </span>
        </li>
      ),
      (
        <li key="menards-example-2">
          Long Version:
          {' '}
          <span className="example-url">
            https://www.menards.com/main/tools-hardware/outdoor-power-equipment/chainsaws-accessories/remington-reg-16-12-amp-electric-chainsaw/p-1444441253821-c-10109.htm
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'napaonline.com',
    retailer_name: 'Napa',
    retailer_db_name: 'Napa',
    regex: [],
    requirements: [
      (
        <li key="napaonline-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must end with the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="napaonline-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          will
          {' '}
          <strong>not</strong>
          {' '}
          contain product information.
        </li>
      ),
      (
        <li key="napaonline-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">https://www.napaonline.com/en/</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="napaonline-example-1">
          <span className="example-url">
            https://www.napaonline.com/en/p/NAFDEF002
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'oreillyauto.com',
    retailer_name: "O'Reilly",
    retailer_db_name: "O'Reilly",
    regex: [],
    requirements: [
      (
        <li key="oreillyauto-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          will
          {' '}
          <strong>not</strong>
          {' '}
          contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="oreillyauto-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must include product and brand information.
        </li>
      ),
      (
        <li key="oreillyauto-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">https://www.oreillyauto.com/detail/c/</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="oreillyauto-example-1">
          <span className="example-url">
            https://www.oreillyauto.com/detail/c/bluedef/bluedef-2-5-gallon-diesel-exhaust-fluid/def0/25gal
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'officedepot.com',
    retailer_name: 'Office Depot',
    retailer_db_name: 'Office Depot',
    regex: [],
    requirements: [
      (
        <li key="officedepot-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="officedepot-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must include product and brand information.
        </li>
      ),
      (
        <li key="officedepot-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">https://www.officedepot.com/a/products/</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="officedepot-example-1">
          <span className="example-url">
            https://www.officedepot.com/a/products/9197728/Epson-EcoTank-ET-3850-All-in/
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'overstock.com',
    retailer_name: 'Overstock',
    retailer_db_name: 'Overstock',
    regex: [],
    requirements: [
      (
        <li key="overstockcom-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">
            https://www.overstock.com/
          </span>
          .
        </li>
      ),
      (
        <li key="overstockcom-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain product and brand information.
        </li>
      ),
      (
        <li key="overstockcom-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain
          {' '}
          <span className="url-fragment">
            product.html
          </span>
          .
        </li>
      ),
      (
        <li key="overstockcom-requirement-4">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          may have
          {' '}
          <span className="url-fragment">
            ?option=[number]
          </span>
          {' '}
          after
          {' '}
          <span className="url-fragment">
            product.html
          </span>
          {' '}
          if the item is a variant. Note that the new option number is
          {' '}
          <b>
            different
          </b>
          {' '}
          from the previous number found in
          {' '}
          <span className="url-fragment">
            ?variant=[number]
          </span>
          .
        </li>
      ),
      (
        <li key="overstockcom-requirement-5">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          may have
          {' '}
          <span className="url-fragment">
            &sku=[sku]
          </span>
          {' '}
          after
          {' '}
          <span className="url-fragment">
            ?option=[number]
          </span>
          {' '}
          if the item is a variant.
        </li>
      ),
    ],
    examples: [
      (
        <li key="overstockcom-example-1">
          <span className="example-url">
            https://www.overstock.com/Home-Garden/Illiana-Indoor-Outdoor-Floral-Pink-Blue-Area-Rug/35413977/product.html?option=67922197
          </span>
        </li>
      ),
      (
        <li key="overstockcom-example-2">
          <span className="example-url">
            https://www.overstock.com/Home-Garden/Illiana-Indoor-Outdoor-Floral-Pink-Blue-Area-Rug/35413977/product.html?option=67922197&sku=39572387-000-005
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'samsclub.com',
    retailer_name: 'Sam\'s Club',
    retailer_db_name: 'Sams Club',
    regex: [],
    requirements: [
      (
        <li key="samsclub-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          will
          {' '}
          <strong>not</strong>
          {' '}
          contain the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="samsclub-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain product information.
        </li>
      ),
      (
        <li key="samsclub-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          will
          {' '}
          <strong>not</strong>
          {' '}
          contain query parameters.
        </li>
      ),
    ],
    examples: [
      (
        <li key="samsclub-example-1">
          <span className="example-url">
            https://www.samsclub.com/ip/sealy-postrepedic-hybrid-highpoint-soft-mattress-twinxl-full-queen-king-calking/13969655926
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'staples.com',
    retailer_name: 'Staples',
    retailer_db_name: 'Staples',
    regex: [],
    requirements: [
      (
        <li key="staplescom-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">
            https://www.staples.com/product_
          </span>
          .
        </li>
      ),
      (
        <li key="staplescom-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="staplescom-example-1">
          <span className="example-url">
            https://www.staples.com/product_24527313
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'supplyhouse.com',
    retailer_name: 'Supply House',
    retailer_db_name: 'Supply House',
    regex: [],
    requirements: [
      (
        <li key="supplyhousecom-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain product information.
        </li>
      ),
    ],
    examples: [
      (
        <li key="supplyhousecom-example-1">
          <span className="example-url">
            https://www.supplyhouse.com/Honeywell-Home-Resideo-TH6220WF2006-T6-Pro-Smart-Wi-Fi-Programmable-Thermostat-2H-2C
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'target.com',
    retailer_name: 'Target',
    retailer_db_name: 'Target',
    regex: [],
    requirements: [
      (
        <li key="target-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          will
          {' '}
          <strong>not</strong>
          {' '}
          contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          , but rather the
          {' '}
          <span className="product-field">TCIN</span>
          . This can be
          found on the product page under
          {' '}
          <span className="url-fragment">Specifications → TCIN</span>
          .
        </li>
      ),
      (
        <li key="target-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          may contain product information.
        </li>
      ),
      (
        <li key="target-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          can be shortened.
        </li>
      ),
    ],
    examples: [
      (
        <li key="target-example-1">
          Short Version:
          {' '}
          <span className="example-url">
            https://www.target.com/p/-/A-54150247
          </span>
        </li>
      ),
      (
        <li key="target-example-2">
          Long Version:
          {' '}
          <span className="example-url">
            https://www.target.com/p/huffy-ladies-nassau-26-cruiser-bike-teal/-/A-54150247
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'tractorsupply.com',
    retailer_name: 'Tractor Supply',
    retailer_db_name: 'Tractor Supply',
    regex: [],
    requirements: [
      (
        <li key="tractorsupply-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          will
          {' '}
          <strong>sometimes</strong>
          {' '}
          contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="tractorsupply-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must contain product information.
        </li>
      ),
      (
        <li key="tractorsupply-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          may end with
          {' '}
          <span className="url-fragment">[Retailer SKU]</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="tractorsupply-example-1">
          <span className="example-url">
            https://www.tractorsupply.com/tsc/product/shelterlogic-automatic-shelter-vent-kit-pack-of-2-1069603
          </span>
        </li>
      ),
      (
        <li key="tractorsupply-example-2">
          <span className="example-url">
            https://www.tractorsupply.com/tsc/product/shelterlogic-10-ft-x-10-ft-sport-pop-up-canopy-slant-leg
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'truevalue.com',
    retailer_name: 'True Value',
    retailer_db_name: 'True Value',
    regex: [],
    requirements: [
      (
        <li key="truevalue-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          will
          {' '}
          <strong>not</strong>
          {' '}
          contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          .
        </li>
      ),
      (
        <li key="truevalue-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          must start with
          {' '}
          <span className="url-fragment">https://www.truevalue.com/product/</span>
          .
        </li>
      ),
      (
        <li key="truevalue-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must contain product information.
        </li>
      ),
      (
        <li key="truevalue-requirement-4">
          <span className="product-field">Product URL</span>
          {' '}
          must end with a trailing slash (
          <span className="url-fragment">/</span>
          ).
        </li>
      ),
    ],
    examples: [
      (
        <li key="truevalue-example-1">
          <span className="example-url">
            https://www.truevalue.com/product/150-ft-x-5-8-inch-hose-hangout-wall-mount-hose-hanger/
          </span>
        </li>
      ),
      (
        <li key="truevalue-example-2">
          <span className="example-url">
            https://www.truevalue.com/product/roof-cap-steel-black-finish-fits-3-or-4-in-round-duct/
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'walmart.ca',
    retailer_name: 'Walmart CA',
    retailer_db_name: 'Walmart - Canada',
    regex: [],
    requirements: [
      (
        <li key="walmartca-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          will not contain the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="walmartca-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain product information.
        </li>
      ),
    ],
    examples: [
      (
        <li key="walmartca-example-1">
          <span className="example-url">
            https://www.walmart.ca/en/ip/rollplay-6v-mini-scooter/6000191047081
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'walmart.com',
    retailer_name: 'Walmart US',
    retailer_db_name: 'Walmart',
    regex: [],
    requirements: [
      (
        <li key="walmart-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          can contain product information, but does not need to.
        </li>
      ),
      (
        <li key="walmart-requirement-2">
          <span className="product-field">
            Product URLs
          </span>
          {' '}
          for this retailer can be shortened.
        </li>
      ),
    ],
    examples: [
      (
        <li key="walmart-example-1">
          Short Version:
          {' '}
          <span className="example-url">
            https://www.walmart.com/ip/seort/252151838?selected=true
          </span>
        </li>
      ),
      (
        <li key="walmart-example-2">
          Long Version:
          {' '}
          <span className="example-url">
            https://www.walmart.com/ip/Design-House-3-Pack-4-in-x-5-8-in-Radius-Oil-Rubbed-Bronze-Door-Hinge-Value-Pack/181712701?selected=true
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'wayfair.ca',
    retailer_name: 'Wayfair CA',
    retailer_db_name: 'Wayfair.ca',
    regex: [],
    requirements: [
      (
        <li key="wayfairca-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="wayfairca-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the product category.
        </li>
      ),
      (
        <li key="wayfairca-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the product name, followed by the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="wayfairca-requirement-4">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            .html
          </span>
          .
        </li>
      ),
      (
        <li key="wayfairca-requirement-5">
          If this item is a variant, its
          {' '}
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            ?piid=[8-10 digit #]
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="wayfairca-example-1">
          Non-variant:
          {' '}
          <span className="example-url">
            https://www.wayfair.ca/appliances/pdp/broan-nutone-arkbws-optional-recirculation-kit-for-bws1-range-hoods-aqgv1184.html
          </span>
        </li>
      ),
      (
        <li key="wayfairca-example-2">
          Variant:
          {' '}
          <span className="example-url">
            https://www.wayfair.ca/appliances/pdp/broan-30-broan-nutone-300cfm-convertible-under-cabinet-range-hood-aqgv1205.html?piid=54371314
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'wayfair.co.uk',
    retailer_name: 'Wayfair UK',
    retailer_db_name: 'Wayfair.co.uk',
    regex: [],
    requirements: [
      (
        <li key="wayfaircouk-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="wayfaircouk-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the product category.
        </li>
      ),
      (
        <li key="wayfaircouk-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the product name, followed by the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="wayfaircouk-requirement-4">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            .html
          </span>
          .
        </li>
      ),
      (
        <li key="wayfaircouk-requirement-5">
          If this item is a variant, its
          {' '}
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            ?piid=[8-10 digit #]
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="wayfaircouk-example-1">
          Non-variant:
          {' '}
          <span className="example-url">
            https://www.wayfair.co.uk/garden/pdp/blue-elephant-iron-bench-u002337345.html
          </span>
        </li>
      ),
      (
        <li key="wayfaircouk-example-2">
          Variant:
          {' '}
          <span className="example-url">
            https://www.wayfair.co.uk/garden/pdp/blue-elephant-alpine-fibreglass-jar-water-fountain-with-light-u002337451.html?piid=1824151954
          </span>
        </li>
      ),
    ],
  },
  {
    retailer_home: 'wayfair.com',
    retailer_name: 'Wayfair US',
    retailer_db_name: 'Wayfair.com',
    regex: [],
    requirements: [
      (
        <li key="wayfair-requirement-1">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="wayfair-requirement-2">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the product category.
        </li>
      ),
      (
        <li key="wayfair-requirement-3">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must contain the product name, followed by the
          {' '}
          <span className="product-field">
            Retailer SKU
          </span>
          .
        </li>
      ),
      (
        <li key="wayfair-requirement-4">
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            .html
          </span>
          .
        </li>
      ),
      (
        <li key="wayfair-requirement-5">
          If this item is a variant, its
          {' '}
          <span className="product-field">
            Product URL
          </span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">
            ?piid=[8-10 digit #]
          </span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="wayfair-example-1">
          Non-variant:
          {' '}
          <span className="example-url">
            https://www.wayfair.com/school-furniture-and-supplies/pdp/suncast-patio-33-gallon-curbside-trash-recycling-bin-xa1386.html
          </span>
        </li>
      ),
      (
        <li key="wayfair-example-2">
          Variant:
          {' '}
          <span className="example-url">
            https://www.wayfair.com/bed-bath/pdp/tempur-pedic-tempur-adapt-11-medium-mattress-sbja1019.html?piid=30305744
          </span>
        </li>
      ),
    ],
  },
];
export const deprecatedRetailerUrlFormats = [
  {
    retailer_home: 'costco.com',
    retailer_name: 'Costco',
    retailer_db_name: 'Costco',
    regex: [],
    requirements: [
      (
        <li key="costco-requirement-1">
          <span className="product-field">Product URL</span>
          {' '}
          must contain the
          {' '}
          <span className="product-field">Retailer SKU</span>
          , which is a 6+ digit number following
          {' '}
          <span className="url-fragment">.product.</span>
          .
        </li>
      ),
      (
        <li key="costco-requirement-2">
          <span className="product-field">Product URL</span>
          {' '}
          may contain a short product description before
          {' '}
          <span className="url-fragment">.product.</span>
          . This description can include lowercase letters, numbers, hyphens,
          and other URL-safe characters.
        </li>
      ),
      (
        <li key="costco-requirement-3">
          <span className="product-field">Product URL</span>
          {' '}
          must end with
          {' '}
          <span className="url-fragment">.html</span>
          .
        </li>
      ),
      (
        <li key="costco-requirement-4">
          <span className="product-field">Product URL</span>
          {' '}
          can be shortened to include only the
          {' '}
          <span className="url-fragment">.product.[Retailer SKU].html</span>
          .
        </li>
      ),
    ],
    examples: [
      (
        <li key="costco-example-1">
          Short Version:
          {' '}
          <span className="example-url">
            https://www.costco.com/.product.1364112.html
          </span>
        </li>
      ),
      (
        <li key="costco-example-2">
          Long Version:
          {' '}
          <span className="example-url">
            https://www.costco.com/cocoon-by-sealy-10-medium-memory-foam-mattress.product.1364112.html
          </span>
        </li>
      ),
    ],
  },
];
export default retailerUrlFormats;
